import { useEffect, useRef } from 'react'
import JSConfetti from 'js-confetti'

/**
 * Configuración de la lluvia de emojis 🔥 que celebra la vista de descuentos.
 * Se deja fuera del componente para que el objeto no se recree en cada render.
 */
const CONFETTI_CONFIG = {
  emojis: ['🔥'],
  emojiSize: 44,
  confettiNumber: 36,
}

/** Tope de espera si el navegador nunca reporta tiempo libre. */
const IDLE_TIMEOUT_MS = 1500

/** Espera de respaldo en navegadores sin `requestIdleCallback` (Safari viejo). */
const FALLBACK_DELAY_MS = 600

/**
 * Programa el disparo para cuando el navegador esté desocupado.
 *
 * El SRP es una página pesada: hidratar el listado y montar los `next/dynamic`
 * toma varios cientos de milisegundos. Arrancar aquí el bucle de animación
 * competiría por el hilo principal justo cuando el usuario espera ver los
 * autos. `requestIdleCallback` cede ese tiempo y dispara en cuanto el navegador
 * se desocupa, con un tope para que nunca se quede sin lanzar.
 *
 * @returns {Function} Función para cancelar el disparo programado.
 */
const scheduleWhenIdle = (callback) => {
  if (typeof window.requestIdleCallback === 'function') {
    const id = window.requestIdleCallback(callback, { timeout: IDLE_TIMEOUT_MS })
    return () => window.cancelIdleCallback(id)
  }

  const id = setTimeout(callback, FALLBACK_DELAY_MS)
  return () => clearTimeout(id)
}

/**
 * Lanza confeti de 🔥 al entrar al SRP con el ordenamiento por descuento.
 *
 * Reglas de disparo (una sola vez por "activación", no en cada render):
 *  - Si el usuario aterriza con el filtro ya aplicado (por ejemplo desde los
 *    CTA de la landing /ultimas-horas), se lanza al montar.
 *  - Si aplica el filtro estando en el SRP, se lanza en ese momento. El SRP usa
 *    navegación shallow, así que el componente no se desmonta: el disparo lo
 *    controla la transición de `active`.
 *  - Mientras el filtro siga aplicado no vuelve a lanzarse, aunque cambien
 *    otros filtros o se pagine.
 *  - Al quitar el filtro se rearma, de modo que volver a aplicarlo celebra otra vez.
 *  - Al recargar la página el componente se monta de nuevo y vuelve a lanzarse,
 *    que es el comportamiento pedido (no se persiste nada en el navegador).
 *
 * `js-confetti` se importa de forma estática: pesa ~12 KB y no toca el DOM al
 * importarse (solo dentro de la clase, que se instancia ya en el efecto), así
 * que no rompe el render de servidor y evita cargar un chunk aparte en caliente.
 *
 * No renderiza marcado: js-confetti inyecta y administra su propio canvas.
 *
 * @param {boolean} active Si el ordenamiento por descuento está aplicado.
 */
const DiscountConfetti = ({ active }) => {
  // `useRef` y no estado: cambiarlo no debe provocar un re-render.
  const hasFiredRef = useRef(false)

  useEffect(() => {
    if (!active) {
      // Se rearma para que reaplicar el filtro vuelva a celebrar.
      hasFiredRef.current = false
      return undefined
    }

    if (hasFiredRef.current) return undefined
    hasFiredRef.current = true

    // Accesibilidad: quien pidió menos animación en su sistema no la recibe.
    const prefersReducedMotion = window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReducedMotion) return undefined

    let instance

    const cancelSchedule = scheduleWhenIdle(() => {
      instance = new JSConfetti()
      instance.addConfetti(CONFETTI_CONFIG)
    })

    return () => {
      cancelSchedule()
      // Retira el canvas si el usuario abandona la vista a media animación.
      instance?.destroyCanvas?.()
    }
  }, [active])

  return null
}

export default DiscountConfetti
